/**
 * JS mirror of tokens.css, for code that cannot read a CSS custom property
 * (chart libraries, canvas, inline style computation). Keep in sync with
 * tokens.css — it is the source of truth for the rendered values.
 */
export const colors = {
  primary: 'var(--mh-color-primary)',
  primaryHover: 'var(--mh-color-primary-hover)',
  primaryContrast: 'var(--mh-color-primary-contrast)',
  accent: 'var(--mh-color-accent)',
  bg: 'var(--mh-color-bg)',
  surface: 'var(--mh-color-surface)',
  surfaceHover: 'var(--mh-color-surface-hover)',
  border: 'var(--mh-color-border)',
  borderStrong: 'var(--mh-color-border-strong)',
  text: 'var(--mh-color-text)',
  textMuted: 'var(--mh-color-text-muted)',
  textInverse: 'var(--mh-color-text-inverse)',
  danger: 'var(--mh-color-danger)',
  warning: 'var(--mh-color-warning)',
  success: 'var(--mh-color-success)',
  info: 'var(--mh-color-info)',
};

/** Keyed by the CollaborationStatus enum the API returns. */
export const statusColors = {
  UNKNOWN: 'var(--mh-color-status-unknown)',
  INTERESTED: 'var(--mh-color-status-interested)',
  CONSULTING: 'var(--mh-color-status-consulting)',
  SUBSCRIBER: 'var(--mh-color-status-subscriber)',
};

/**
 * The activity-colour palette, as a SLOT LIST rather than a map.
 *
 * Which activity types exist is deployment data (product-neutrality rule), so
 * nothing here — and nothing in app code — may name one. The client picks a
 * slot by a type's position in the vocabulary, which means a deployment that
 * renames «نصب و راه‌اندازی» keeps its colour, and one that adds a ninth type
 * wraps around rather than losing its colour entirely.
 */
export const activityPalette = [
  'var(--mh-color-activity-1)',
  'var(--mh-color-activity-2)',
  'var(--mh-color-activity-3)',
  'var(--mh-color-activity-4)',
  'var(--mh-color-activity-5)',
  'var(--mh-color-activity-6)',
  'var(--mh-color-activity-7)',
  'var(--mh-color-activity-8)',
];

export const radius = {
  base: 'var(--mh-radius)',
  full: 'var(--mh-radius-full)',
};

export const density = {
  rowHeight: 'var(--mh-density-row-height)',
  rowPaddingY: 'var(--mh-density-row-padding-y)',
  hour: 'var(--mh-density-hour)',
  timeGutter: 'var(--mh-density-time-gutter)',
  drawerWidth: 'var(--mh-density-drawer-width)',
  rowPaddingX: 'var(--mh-density-row-padding-x)',
  headerHeight: 'var(--mh-density-header-height)',
  controlHeight: 'var(--mh-density-control-height)',
  toolbarHeight: 'var(--mh-density-toolbar-height)',
  sidebarWidth: 'var(--mh-density-sidebar-width)',
  overlayWidth: 'var(--mh-density-overlay-width)',
  overlayWidthWide: 'var(--mh-density-overlay-wide)',
  thumb: 'var(--mh-density-thumb)',
  mediaPreview: 'var(--mh-density-media-preview)',
  avatar: 'var(--mh-density-avatar)',
  chartBar: 'var(--mh-density-chart-bar)',
  chartLabel: 'var(--mh-density-chart-label)',
  chartValue: 'var(--mh-density-chart-value)',
  filterWidth: 'var(--mh-density-filter-width)',
  filterSectionMax: 'var(--mh-density-filter-section-max)',
};

export const typography = {
  fontFamily: 'var(--mh-font-family)',
  sizeXs: 'var(--mh-font-size-xs)',
  sizeSm: 'var(--mh-font-size-sm)',
  sizeBase: 'var(--mh-font-size-base)',
  sizeMd: 'var(--mh-font-size-md)',
  sizeLg: 'var(--mh-font-size-lg)',
  sizeXl: 'var(--mh-font-size-xl)',
};

/**
 * Curated palette offered when someone picks a tag colour.
 *
 * Tag colours are *data* — an admin chooses them and they are stored per tag —
 * so unlike every other colour in the system they cannot be CSS custom
 * properties. What the design system still owns is which colours are on offer
 * and which one a new tag starts with, so tags stay visually coherent instead
 * of drifting into arbitrary hexes. Literal values because `<input type=color>`
 * cannot take a `var()`.
 *
 * These mirror the feedback/status hues in tokens.css; keep them in step.
 */
export const tagPalette = [
  { name: 'teal', value: '#1d6f6a' },
  { name: 'terracotta', value: '#c2703d' },
  { name: 'amber', value: '#b45309' },
  { name: 'green', value: '#15803d' },
  { name: 'blue', value: '#1d4ed8' },
  { name: 'red', value: '#b91c1c' },
  { name: 'violet', value: '#7c3aed' },
  { name: 'pink', value: '#db2777' },
  { name: 'slate', value: '#6a7674' },
];

/** What a brand-new tag starts as, before the admin picks something else. */
export const defaultTagColor = tagPalette[0].value;
